import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import CircularProfileImage from '../components/CircularProfileImage';

const CONTRACTOR_SERVICES = [
  { value: 'builders', label: 'Builders' },
  { value: 'tilers', label: 'Tilers' },
  { value: 'roofers', label: 'Roofers' },
  { value: 'plumbers', label: 'Plumbers' },
  { value: 'electricians', label: 'Electricians' },
  { value: 'architects', label: 'Architects' },
  { value: 'painters', label: 'Painters' },
  { value: 'landscapers', label: 'Landscapers' },
  { value: 'carpenters', label: 'Carpenters' }
];

const ContractorProfile = () => {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (contractorId) {
      fetchContractorData();
    }
  }, [contractorId]);

  const fetchContractorData = async () => {
    try {
      setLoading(true);
      const [contractorRes, portfolioRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/contractors/${contractorId}`),
        axios.get(`${API}/portfolios/contractor/${contractorId}`),
        axios.get(`${API}/contractor-reviews/${contractorId}`)
      ]);

      setContractor(contractorRes.data);
      setPortfolios(portfolioRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching contractor:', error);
      toast.error('Failed to load contractor profile');
      navigate('/home-services');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i 
          key={i} 
          className={`fas fa-star ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
        ></i>
      );
    }
    return stars;
  };

  const getServiceLabel = (value) => {
    const service = CONTRACTOR_SERVICES.find(s => s.value === value);
    return service ? service.label : value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-emerald-600"></i>
          <p className="mt-4 text-gray-600">Loading contractor profile...</p>
        </div>
      </main>
    );
  }

  if (!contractor) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <Card className="text-center p-8">
          <i className="fas fa-user-slash text-6xl text-gray-300 mb-4"></i>
          <h2 className="text-xl font-semibold mb-2">Contractor Not Found</h2>
          <Button onClick={() => navigate('/home-services')}>
            Back to Home Services
          </Button>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/home-services')} 
          className="mb-4"
          data-testid="back-btn"
        >
          <i className="fas fa-arrow-left mr-2"></i> Back to Home Services
        </Button>

        {/* Profile Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar / Logo */}
              <div className="flex-shrink-0">
                <CircularProfileImage
                  src={contractor.profileImage}
                  alt={contractor.businessName}
                  size="lg"
                  editable={false}
                />
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-testid="contractor-name">
                      {contractor.businessName}
                      {contractor.isVerified && (
                        <Badge className="bg-blue-100 text-blue-700">
                          <i className="fas fa-check-circle mr-1"></i> Verified
                        </Badge>
                      )}
                    </h1>
                    <p className="text-gray-600">{contractor.contractorName}</p>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex">{renderStars(Math.round(contractor.averageRating || 0))}</div>
                  <span className="text-gray-600">
                    {contractor.averageRating?.toFixed(1) || '0.0'} ({contractor.totalReviews || 0} reviews)
                  </span>
                </div>

                {/* Services */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {contractor.services?.map((service) => (
                    <Badge key={service} variant="outline" className="bg-emerald-50">
                      {getServiceLabel(service)}
                    </Badge>
                  ))}
                </div>

                {/* Location */}
                <div className="mt-4 text-gray-600">
                  <p>
                    <i className="fas fa-map-marker-alt text-emerald-600 mr-2"></i>
                    <strong>Location:</strong> {contractor.location}
                  </p>
                  {contractor.areasServed?.length > 0 && (
                    <p className="mt-1">
                      <i className="fas fa-map text-emerald-600 mr-2"></i>
                      <strong>Areas Served:</strong> {contractor.areasServed.join(', ')}
                    </p>
                  )}
                </div>

                {/* Description */}
                {contractor.description && (
                  <p className="mt-4 text-gray-600">{contractor.description}</p>
                )}

                {/* Contact Buttons */}
                <div className="flex flex-wrap gap-3 mt-6">
                  <a
                    href={`https://wa.me/${contractor.whatsappNumber?.replace(/\D/g, '')}?text=Hi, I found your profile on Flippy Homes and would like to discuss a project.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-contact"
                  >
                    <Button className="bg-green-600 hover:bg-green-700">
                      <i className="fab fa-whatsapp mr-2"></i> WhatsApp
                    </Button>
                  </a>
                  <a href={`tel:${contractor.contactNumber}`} data-testid="phone-contact">
                    <Button variant="outline">
                      <i className="fas fa-phone mr-2"></i> Call
                    </Button>
                  </a>
                  <a href={`mailto:${contractor.email}`} data-testid="email-contact">
                    <Button variant="outline">
                      <i className="fas fa-envelope mr-2"></i> Email
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Portfolio & Reviews */}
        <Tabs defaultValue="portfolio" className="w-full">
          <TabsList className="w-full grid grid-cols-2 mb-6">
            <TabsTrigger value="portfolio" data-testid="portfolio-tab">
              <i className="fas fa-images mr-2"></i> Portfolio ({portfolios.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="reviews-tab">
              <i className="fas fa-star mr-2"></i> Reviews ({reviews.length})
            </TabsTrigger>
          </TabsList>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            {portfolios.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <i className="fas fa-images text-5xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">No portfolio items yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {portfolios.map((project) => (
                  <Card key={project.id} className="overflow-hidden" data-testid={`portfolio-${project.id}`}>
                    {/* Project Images */}
                    {project.images?.length > 0 && (
                      <div className="relative h-48 bg-gray-100">
                        <img
                          src={project.images[0]}
                          alt={project.projectTitle}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => setSelectedImage(project.images[0])}
                        />
                        {project.images.length > 1 && (
                          <Badge className="absolute bottom-2 right-2 bg-black/60">
                            +{project.images.length - 1} more
                          </Badge>
                        )}
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{project.projectTitle}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Badge variant="outline">{getServiceLabel(project.serviceType)}</Badge>
                        <span>•</span>
                        <span>{project.location}</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm mb-2">{project.description}</p>
                      <p className="text-xs text-gray-400">
                        <i className="fas fa-calendar mr-1"></i>
                        Completed: {formatDate(project.completionDate)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            {reviews.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <i className="fas fa-star text-5xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">No reviews yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card key={review.id} data-testid={`review-${review.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold">{review.userName}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex">{renderStars(review.rating)}</div>
                            <span className="text-sm text-gray-500">
                              {formatDate(review.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="mt-3 text-gray-600">{review.review}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh]">
              <img 
                src={selectedImage} 
                alt="Portfolio" 
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
              <button
                className="absolute top-2 right-2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2"
                onClick={() => setSelectedImage(null)}
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ContractorProfile;
